import { FeedLetConfig, FeedbackReport, ExitSurvey, ContextualMarker, ViewportInfo } from './types'
import { FloatingButton } from './floating-button'
import { ThreadInterface, ThreadData } from './thread-interface'
import { ExitIntentDetector } from './exit-intent'
import { ScreenshotCapture } from './screenshot'
import { SupabaseClient } from './supabase'
import { ContextualFeedbackManager } from './contextual-feedback'
import { ThemeManager } from './theme-manager'

export class FeedLetWidget {
  private config: FeedLetConfig
  private floatingButton: FloatingButton
  private threadInterface: ThreadInterface
  private exitIntentDetector: ExitIntentDetector | null = null
  private screenshotCapture: ScreenshotCapture
  private supabaseClient: SupabaseClient
  private contextualManager: ContextualFeedbackManager
  private themeManager: ThemeManager
  private eventListeners: Map<string, Function[]> = new Map()
  private isInitialized = false
  private currentThreadData: ThreadData | null = null

  constructor(config: FeedLetConfig) {
    this.config = {
      enableExitIntent: true,
      enableFeedback: true,
      enableContextualFeedback: true,
      exitIntentSensitivity: 2,
      position: 'bottom-right',
      color: '#4f46e5',
      ...config
    }

    this.themeManager = new ThemeManager()
    this.floatingButton = new FloatingButton(this.config)
    this.threadInterface = new ThreadInterface(this.config)
    this.screenshotCapture = new ScreenshotCapture()
    this.supabaseClient = new SupabaseClient(this.config)
    this.contextualManager = new ContextualFeedbackManager()

    this.handleThreadSubmit = this.handleThreadSubmit.bind(this)
    this.handleContextualMarker = this.handleContextualMarker.bind(this)
  }

  init(): void {
    if (this.isInitialized) return

    try {
      this.setupTheme()
      this.setupFloatingButton()
      this.setupThreadInterface()
      this.setupExitIntent()
      this.setupContextualFeedback()

      // Check for comment view mode in URL
      this.checkForCommentViewMode()

      this.isInitialized = true
      this.emit('widget-initialized', {})
    } catch (error) {
      console.error('FeedLet: Failed to initialize widget', error)
    }
  }

  private checkForCommentViewMode(): void {
    const urlParams = new URLSearchParams(window.location.search)
    const commentId = urlParams.get('feedlet_comment')
    const viewMode = urlParams.get('feedlet_view')
    
    if (commentId && viewMode === 'comment') {
      console.log('FeedLet: Comment view mode detected, loading comment:', commentId)
      this.loadAndDisplayComment(commentId)
    }
  }

  private async loadAndDisplayComment(commentId: string): Promise<void> {
    try {
      console.log('FeedLet: Loading comment data for ID:', commentId)
      
      // Load comment data from database
      const commentData = await this.supabaseClient.getCommentById(commentId)
      
      if (commentData && commentData.is_contextual) {
        console.log('FeedLet: Displaying contextual comment:', commentData)
        this.displayCommentOverlay(commentData)
        
        // Hide the floating button in view mode
        this.floatingButton.hide()
        
        // Add a "Back to Dashboard" button
        this.addBackToDashboardButton()
      }
    } catch (error) {
      console.error('FeedLet: Failed to load comment:', error)
    }
  }

  private displayCommentOverlay(commentData: any): void {
    // Create comment overlay at the original position
    const overlay = document.createElement('div')
    overlay.id = 'feedlet-comment-overlay'
    overlay.className = 'feedlet-comment-replay'
    
    // Calculate position accounting for potential scroll differences
    const x = commentData.position_x || 0
    const y = commentData.position_y || 0
    
    overlay.style.cssText = `
      position: fixed;
      left: ${x}px;
      top: ${y}px;
      z-index: 1000000;
      pointer-events: none;
    `
    
    overlay.innerHTML = `
      <div class="feedlet-comment-pin">
        <div class="feedlet-comment-pin-icon">
          📍
        </div>
        <div class="feedlet-comment-bubble">
          <div class="feedlet-comment-header">
            <strong>Comment</strong>
            <span class="feedlet-comment-date">${new Date(commentData.created_at).toLocaleDateString()}</span>
          </div>
          <div class="feedlet-comment-content">
            ${this.escapeHtml(commentData.note)}
          </div>
          ${commentData.element_text ? `
            <div class="feedlet-comment-context">
              <small>On element: "${this.escapeHtml(commentData.element_text)}"</small>
            </div>
          ` : ''}
        </div>
      </div>
    `
    
    // Add styles for comment overlay
    this.addCommentOverlayStyles()
    
    // Try to highlight the original element if possible
    if (commentData.element_selector) {
      this.highlightElement(commentData.element_selector)
    }
    
    document.body.appendChild(overlay)
    
    // Animate in
    requestAnimationFrame(() => {
      overlay.classList.add('visible')
    })
  }

  private highlightElement(selector: string): void {
    try {
      const element = document.querySelector(selector)
      if (element) {
        element.classList.add('feedlet-highlighted-element')
        console.log('FeedLet: Highlighted element:', selector)
      }
    } catch (error) {
      console.warn('FeedLet: Could not highlight element:', selector, error)
    }
  }

  private addCommentOverlayStyles(): void {
    if (document.getElementById('feedlet-comment-overlay-styles')) return
    
    const style = document.createElement('style')
    style.id = 'feedlet-comment-overlay-styles'
    style.textContent = `
      .feedlet-comment-replay {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        opacity: 0;
        transform: scale(0.8) translateY(10px);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      .feedlet-comment-replay.visible {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
      
      .feedlet-comment-pin {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      
      .feedlet-comment-pin-icon {
        font-size: 24px;
        margin-bottom: 8px;
        animation: feedlet-pin-pulse 2s infinite;
      }
      
      .feedlet-comment-bubble {
        background: #1a1a1a;
        color: white;
        border-radius: 12px;
        padding: 16px;
        max-width: 300px;
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
        border: 1px solid #333;
        pointer-events: auto;
        position: relative;
      }
      
      .feedlet-comment-bubble::before {
        content: '';
        position: absolute;
        top: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-bottom: 8px solid #1a1a1a;
      }
      
      .feedlet-comment-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        font-size: 14px;
      }
      
      .feedlet-comment-date {
        color: #999;
        font-size: 12px;
      }
      
      .feedlet-comment-content {
        font-size: 14px;
        line-height: 1.4;
        margin-bottom: 8px;
      }
      
      .feedlet-comment-context {
        color: #999;
        font-size: 12px;
        font-style: italic;
        border-top: 1px solid #333;
        padding-top: 8px;
      }
      
      .feedlet-highlighted-element {
        background: rgba(79, 70, 229, 0.2) !important;
        outline: 2px solid #4f46e5 !important;
        outline-offset: 2px !important;
        border-radius: 4px !important;
      }
      
      @keyframes feedlet-pin-pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
      
      .feedlet-back-to-dashboard {
        position: fixed;
        top: 20px;
        right: 20px;
        background: #1a1a1a;
        color: white;
        border: 1px solid #333;
        border-radius: 8px;
        padding: 12px 16px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        cursor: pointer;
        z-index: 1000001;
        text-decoration: none;
        transition: all 0.2s ease;
      }
      
      .feedlet-back-to-dashboard:hover {
        background: #333;
        transform: translateY(-1px);
      }
    `
    
    document.head.appendChild(style)
  }

  private addBackToDashboardButton(): void {
    const backButton = document.createElement('a')
    backButton.href = this.config.dashboardUrl || 'http://localhost:3000'
    backButton.className = 'feedlet-back-to-dashboard'
    backButton.textContent = '← Back to Dashboard'
    backButton.target = '_blank'
    
    document.body.appendChild(backButton)
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  private setupFloatingButton(): void {
    const element = this.floatingButton.create(
      () => this.openFeedbackModal(),
      () => this.toggleContextualComments()
    )
    
    document.body.appendChild(element)
  }

  private setupThreadInterface(): void {
    this.threadInterface.on('submit', (data: any) => this.handleThreadSubmit(data))
    this.threadInterface.on('opened', (data: any) => this.emit('widget-opened', data))
    this.threadInterface.on('closed', () => this.emit('widget-closed', {}))
  }

  private setupExitIntent(): void {
    if (!this.config.enableExitIntent) return

    this.exitIntentDetector = new ExitIntentDetector({
      sensitivity: this.config.exitIntentSensitivity || 2,
      delay: 500,
      onExitIntent: () => {
        this.openExitSurveyModal()
      }
    })

    this.exitIntentDetector.start()
  }

  private setupContextualFeedback(): void {
    if (!this.config.enableContextualFeedback) return
    // No additional setup needed - contextual manager is ready
  }

  private setupTheme(): void {
    // Apply initial theme
    this.themeManager.applyCSSVariables(this.config.color)
    
    // Listen for theme changes
    this.themeManager.onThemeChange((theme) => {
      this.handleThemeChange(theme)
    })
  }

  private handleThemeChange(theme: 'light' | 'dark'): void {
    // Reapply CSS variables when theme changes
    this.themeManager.applyCSSVariables(this.config.color)
    
    // Emit theme change event for components to listen to
    this.emit('theme-changed', { theme })
  }

  private toggleContextualComments(): void {
    if (!this.contextualManager.isContextualModeEnabled()) {
      console.log('FeedLet: Enabling contextual comments mode')
      this.contextualManager.enable(this.handleContextualMarker)
      this.floatingButton.updateContextualMode(true)
      this.emit('contextual-mode-enabled', {})
      
      // Show a brief instruction to the user
      this.showContextualModeInstruction()
    } else {
      console.log('FeedLet: Disabling contextual comments mode')
      this.contextualManager.disable()
      this.floatingButton.updateContextualMode(false)
      this.emit('contextual-mode-disabled', {})
      this.hideContextualModeInstruction()
    }
  }

  private showContextualModeInstruction(): void {
    // Remove any existing instruction
    this.hideContextualModeInstruction()
    
    const instruction = document.createElement('div')
    instruction.id = 'feedlet-contextual-instruction'
    instruction.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #1a1a1a;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        z-index: 1000001;
        border: 1px solid #333;
        animation: feedlet-slide-down 0.3s ease-out;
      ">
        📍 Click anywhere on the page to add a comment
        <button style="
          background: none;
          border: none;
          color: #999;
          margin-left: 12px;
          cursor: pointer;
          font-size: 16px;
        " onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `
    
    // Add animation styles
    const style = document.createElement('style')
    style.textContent = `
      @keyframes feedlet-slide-down {
        from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
        to { opacity: 1; transform: translateX(-50%) translateY(0); }
      }
    `
    document.head.appendChild(style)
    
    document.body.appendChild(instruction)
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.hideContextualModeInstruction()
    }, 5000)
  }

  private hideContextualModeInstruction(): void {
    const instruction = document.getElementById('feedlet-contextual-instruction')
    if (instruction) {
      instruction.remove()
    }
  }

  private refreshFloatingButton(): void {
    // Ensure floating button is in correct state after contextual mode reset
    console.log('FeedLet: Refreshing floating button state')
    this.floatingButton.updateContextualMode(false)
  }

  private openCommentModal(): void {
    // This method is no longer needed as we use contextual comments
    // But keeping it for backward compatibility
    this.toggleContextualComments()
  }

  private openFeedbackModal(): void {
    if (this.contextualManager.isContextualModeEnabled()) {
      this.contextualManager.disable()
      this.floatingButton.updateContextualMode(false)
    }

    // Position the thread interface in the center of the screen
    const position = {
      x: window.innerWidth / 2 - 180, // Center horizontally (360px width / 2)
      y: window.innerHeight / 2 - 200  // Center vertically
    }

    const threadData: ThreadData = {
      type: 'feedback',
      position
    }

    this.threadInterface.open(threadData, () => {
      this.emit('widget-closed', {})
    })
  }

  private openExitSurveyModal(): void {
    // Position the thread interface in the center of the screen
    const position = {
      x: window.innerWidth / 2 - 180, // Center horizontally
      y: window.innerHeight / 2 - 200  // Center vertically
    }

    const threadData: ThreadData = {
      type: 'exit-survey', // Use dedicated exit survey type
      position,
      placeholder: this.config.customTexts?.exitSurveyQuestion || 'We noticed you might be leaving. What could we have done better?'
    }

    // Store current thread data for later reference
    this.currentThreadData = threadData

    this.threadInterface.open(threadData, () => {
      this.emit('widget-closed', {})
    })
  }

  private handleContextualMarker(marker: ContextualMarker): void {
    console.log('FeedLet: handleContextualMarker called with marker:', marker)
    
    // Disable contextual mode first to prevent event conflicts
    console.log('FeedLet: Disabling contextual mode before opening thread interface')
    this.contextualManager.disable()
    this.floatingButton.updateContextualMode(false)
    
    const viewportInfo = this.contextualManager.getViewportInfo()
    
    const threadData: ThreadData = {
      type: 'contextual',
      position: { x: marker.x, y: marker.y },
      marker,
      viewportInfo
    }

    console.log('FeedLet: Opening thread interface with data:', threadData)
    this.threadInterface.open(threadData, () => {
      this.emit('widget-closed', {})
    })
  }

  private async handleThreadSubmit(data: any): Promise<void> {
    try {
      console.log('FeedLet: handleThreadSubmit called with data:', data)
      
      const currentUrl = window.location.href
      const userAgent = navigator.userAgent

      if (data.threadType === 'exit-survey' || this.currentThreadData?.type === 'exit-survey') {
        // Exit survey submission
        console.log('FeedLet: Submitting exit survey')
        await this.submitExitSurvey({
          question: this.config.customTexts?.exitSurveyQuestion || 'We noticed you might be leaving. What could we have done better?',
          answer: data.answer || data.note,
          page_url: currentUrl,
          user_agent: userAgent,
          project_id: this.config.projectId,
          // Contact information
          contact_email: data.contact_email || null,
          notify_on_update: data.notify_on_update || false
        })
      } else {
        // Feedback submission (regular or contextual)
        console.log('FeedLet: Submitting feedback')
        
        // Upload screenshots first if any exist
        let screenshotUrls: string[] = []
        if (data.screenshots && data.screenshots.length > 0) {
          console.log('FeedLet: Uploading', data.screenshots.length, 'screenshots...')
          try {
            screenshotUrls = await this.supabaseClient.uploadScreenshots(data.screenshots)
            console.log('FeedLet: Screenshots uploaded successfully:', screenshotUrls)
          } catch (uploadError) {
            console.error('FeedLet: Screenshot upload failed:', uploadError)
            // Continue with feedback submission even if screenshot upload fails
          }
        }
        
        // Determine if this is contextual feedback by checking current thread data
        const isContextualComment = data.marker || data.viewportInfo || data.isContextual
        
        const feedbackData: any = {
          type: data.type || 'other',
          note: data.note,
          page_url: currentUrl,
          user_agent: userAgent,
          project_id: this.config.projectId,
          screenshots: screenshotUrls, // New: multiple screenshots
          screenshot_url: screenshotUrls.length > 0 ? screenshotUrls[0] : null, // Backward compatibility
          // Contact information
          contact_email: data.contact_email || null,
          notify_on_update: data.notify_on_update || false
        }

        // Add contextual data if available
        if (isContextualComment && data.marker) {
          feedbackData.is_contextual = true
          feedbackData.position_x = data.marker.x
          feedbackData.position_y = data.marker.y
          feedbackData.element_selector = data.marker.elementSelector
          feedbackData.element_text = data.marker.elementText
          
          if (data.viewportInfo) {
            feedbackData.viewport_width = data.viewportInfo.width
            feedbackData.viewport_height = data.viewportInfo.height
            feedbackData.scroll_x = data.viewportInfo.scrollX || 0
            feedbackData.scroll_y = data.viewportInfo.scrollY || 0
          }
          
          console.log('FeedLet: Contextual comment data:', {
            position: { x: data.marker.x, y: data.marker.y },
            element: data.marker.elementSelector,
            viewport: data.viewportInfo
          })
        } else {
          feedbackData.is_contextual = false
        }

        console.log('FeedLet: Complete feedback data:', feedbackData)
        
        try {
          await this.supabaseClient.saveFeedbackReport(feedbackData)
          console.log('FeedLet: Submission successful')
        } catch (submitError) {
          console.error('FeedLet: Submission failed:', submitError)
          throw submitError
        }
      }

      console.log('FeedLet: Submission successful')
      
      // Show success message
      this.showThreadSuccess()
      
      // Close thread interface after delay and reset widget state
      setTimeout(() => {
        this.threadInterface.close()
        
        // Reset contextual mode if it was enabled
        if (this.contextualManager.isContextualModeEnabled()) {
          console.log('FeedLet: Resetting contextual mode after submission')
          this.contextualManager.disable()
          this.floatingButton.updateContextualMode(false)
          this.hideContextualModeInstruction()
        }
        
        // Force refresh floating button to ensure it's responsive
        this.refreshFloatingButton()
      }, 1000)

    } catch (error) {
      console.error('FeedLet: Failed to submit data', error)
      const errorObj = error as Error
      console.error('FeedLet: Error details:', {
        name: errorObj.name || 'Unknown',
        message: errorObj.message || 'Unknown error',
        stack: errorObj.stack || 'No stack trace'
      })
      this.showThreadError('Failed to send feedback. Please try again.')
    }
  }

  private showThreadSuccess(): void {
    const threadElement = document.getElementById('feedlet-thread')
    if (threadElement) {
      const actionsContainer = threadElement.querySelector('.feedlet-thread-actions')
      if (actionsContainer) {
        actionsContainer.innerHTML = `
          <div style="display: flex; align-items: center; gap: 8px; color: #22c55e; font-weight: 500; font-size: 14px;">
            <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
            Sent successfully!
          </div>
        `
      }
    }
  }

  private showThreadError(message: string): void {
    const threadElement = document.getElementById('feedlet-thread')
    if (threadElement) {
      const actionsContainer = threadElement.querySelector('.feedlet-thread-actions')
      if (actionsContainer) {
        // Show error message
        const errorDiv = document.createElement('div')
        errorDiv.style.cssText = 'color: #ef4444; font-size: 12px; margin-bottom: 8px;'
        errorDiv.textContent = message
        actionsContainer.insertBefore(errorDiv, actionsContainer.firstChild)
        
        // Re-enable submit button
        const submitBtn = actionsContainer.querySelector('.feedlet-thread-submit-btn') as HTMLButtonElement
        if (submitBtn) {
          submitBtn.disabled = false
          submitBtn.innerHTML = `
            <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"></path>
            </svg>
          `
        }
        
        // Remove error message after 5 seconds
        setTimeout(() => {
          errorDiv.remove()
        }, 5000)
      }
    }
  }

  private getCurrentContextualMarker(): ContextualMarker | null {
    const markers = this.contextualManager.getMarkers()
    return markers.length > 0 ? markers[markers.length - 1] : null
  }

  private async submitFeedback(feedback: FeedbackReport): Promise<void> {
    await this.supabaseClient.saveFeedbackReport(feedback)
    this.emit('feedback-submitted', feedback)
  }

  private async submitExitSurvey(survey: ExitSurvey): Promise<void> {
    await this.supabaseClient.saveExitSurvey(survey)
    this.emit('exit-survey-submitted', survey)
  }

  // Public API methods
  public showFeedbackModal(): void {
    this.openFeedbackModal()
  }

  public enableContextualComments(): void {
    if (!this.contextualManager.isContextualModeEnabled()) {
      this.toggleContextualComments()
    }
  }

  public showCommentModal(): void {
    // For backward compatibility, enable contextual comments
    this.enableContextualComments()
  }

  public enableContextualFeedback(): void {
    this.enableContextualComments()
  }

  public disableContextualFeedback(): void {
    if (this.contextualManager.isContextualModeEnabled()) {
      console.log('FeedLet: Disabling contextual feedback mode')
      this.contextualManager.disable()
      this.floatingButton.updateContextualMode(false)
      this.emit('contextual-mode-disabled', {})
      this.hideContextualModeInstruction()
    }
  }

  public async testConnection(): Promise<boolean> {
    try {
      console.log('FeedLet: Testing Supabase connection...')
      const isConnected = await this.supabaseClient.testConnection()
      console.log('FeedLet: Connection test result:', isConnected)
      return isConnected
    } catch (error) {
      console.error('FeedLet: Connection test failed:', error)
      return false
    }
  }

  public enableContextualMode(): void {
    this.enableContextualFeedback()
  }

  public disableContextualMode(): void {
    this.disableContextualFeedback()
  }

  public isContextualModeEnabled(): boolean {
    return this.contextualManager.isContextualModeEnabled()
  }

  public on(event: string, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, [])
    }
    this.eventListeners.get(event)!.push(callback)
  }

  public off(event: string, callback: Function): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      const index = listeners.indexOf(callback)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }

  private emit(event: string, data: any): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      listeners.forEach(callback => callback(data))
    }
  }

  public hide(): void {
    this.floatingButton.hide()
  }

  public show(): void {
    this.floatingButton.show()
  }

  public destroy(): void {
    if (this.exitIntentDetector) {
      this.exitIntentDetector.stop()
    }

    this.contextualManager.disable()
    this.floatingButton.destroy()
    this.threadInterface.destroy()
    this.themeManager.destroy()
    
    this.isInitialized = false
    this.emit('widget-destroyed', {})
  }
} 